import axios from '../axios';
import environment from '../environments/index';
import { fireAnalytics } from '../utils/firebase';
import { getSessionId, getDeviceCategory } from '../utils/analyticsHelpers.ts';
import type { DocumentInsight } from './projectAutofillDraftService.ts';
import {
  documentationPipelinePhaseForLoader,
  waitForProjectAutofillDocumentationPipeline
} from './projectAutofillDraftService.ts';

export type ChatbotSuggestedAction = { actionId: string; label: string }

export type CanonicalChatTurnMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  actionId?: string | null;
  meta?: Record<string, unknown> | null;
};

export interface LLMAnalysisResult {
  actionId: string | null;
  activeAgent?: string;
  confidence: number;
  response: string;
  conversationId?: string;
  userMessage?: CanonicalChatTurnMessage;
  assistantMessage?: CanonicalChatTurnMessage;
  guideMode?: boolean;
  needsData?: boolean;
  data?: any;
  handoffContext?: { userIntent?: string };
  suggestedActions?: ChatbotSuggestedAction[];
  total?: number;
  noResultsMessage?: string;
  quotationType?: string;
  workVisibility?: string;
  isPublic?: boolean;
  collectedData?: Record<string, any>;
  draftId?: string;
  nextAction?: 'complete_draft' | 'upload_docs' | string;
  validation?: {
    blockingIssues?: string[];
    warnings?: string[];
  };
  draft?: any;
}

export interface ConversationMessage {
  role: 'user' | 'assistant';
  content: string;
  actionId?: string | null;
}

export interface AnalyzeWithLLMOptions {
  activeAgent?: string;
  handoffContext?: { userIntent: string };
  collectedData?: Record<string, any>;
  draftId?: string;
  imageUploaded?: boolean;
  readyToExecute?: boolean;
  image?: Blob | File;
  documentationFiles?: File[];
  conversationId?: string;
  clientSessionId?: string;
  projectAutofillFinalize?: boolean;
}

export interface LLMServiceError extends Error {
  code?: string;
  status?: number;
  maxBytes?: number;
  userMessage?: string;
  documentInsight?: DocumentInsight | null;
}

export const PROJECT_AUTOFILL_CHAT_MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;

export const CHATBOT_PROJECT_AUTOFILL_DOCUMENTATION_MAX_FILES = 12;

export const PROJECT_AUTOFILL_STREAM_INACTIVITY_MS = 120000;

export const PROJECT_AUTOFILL_STREAM_MAX_DURATION_MS = 600000;

export type ProjectAutofillStreamPipelineEvent =
  | {
      kind: 'progress'
      phase: string
      draftId?: string
      primaryFileOriginalName?: string | null
      primaryFileSizeBytes?: number | null
    }
  | { kind: 'heartbeat'; phase?: string };

export function isPdfLikeAttachment(file: File | Blob): boolean {
  if (file.type === 'application/pdf') {
    return true;
  }
  if (file instanceof File) {
    const n = (file.name || '').toLowerCase();
    return n.endsWith('.pdf');
  }
  return false;
}

type NdjsonChatbotEvent =
  | { type: 'progress'; phase: string; draftId?: string }
  | { type: 'complete'; data: LLMAnalysisResult }
  | { type: 'error'; message?: string; code?: string }
  | { type: 'heartbeat'; phase?: string }

function buildChatbotMessageFormData(
  query: string,
  history: ConversationMessage[],
  options?: AnalyzeWithLLMOptions
): FormData {
  const formData = new FormData();
  formData.append('message', query);
  formData.append('history', JSON.stringify(history));
  const activeAgent = options?.activeAgent || 'general';
  if (
    typeof options?.conversationId === 'string' &&
    /^\d+$/.test(options.conversationId.trim())
  ) {
    formData.append('conversationId', options.conversationId.trim());
  } else if (options?.clientSessionId && options.clientSessionId.trim().length > 0) {
    formData.append('clientSessionId', options.clientSessionId.trim());
  }
  formData.append('activeAgent', activeAgent);
  if (options?.handoffContext) {
    formData.append('handoffContext', JSON.stringify(options.handoffContext));
  }
  if (options?.collectedData) {
    formData.append('collectedData', JSON.stringify(options.collectedData));
  }
  const draftId = options?.draftId || options?.collectedData?.draftId;
  if (draftId) {
    formData.append('draftId', String(draftId));
  }
  const imageUploaded = options?.imageUploaded ?? false;
  formData.append('image_uploaded', imageUploaded ? 'true' : 'false');
  const readyToExecute = options?.readyToExecute ?? false;
  formData.append('readyToExecute', readyToExecute ? 'true' : 'false');
  const documentationSlots =
    options?.documentationFiles?.filter((f) => f && f.size > 0) ?? [];
  const useProjectAutofillDocumentation =
    activeAgent === 'project-autofill' &&
    documentationSlots.length > 0 &&
    options?.projectAutofillFinalize !== true;
  if (useProjectAutofillDocumentation) {
    for (const doc of documentationSlots) {
      const named = doc as File;
      formData.append('documentation', doc, named.name || 'document');
    }
  } else if (options?.image) {
    const img = options.image as File;
    const isVideo = img.type?.startsWith('video/');
    const isPdf = isPdfLikeAttachment(img);
    formData.append(
      isVideo ? 'video' : isPdf ? 'file' : 'image',
      img,
      isVideo ? (img.name || 'video.mp4') : isPdf ? (img.name || 'document.pdf') : 'image.jpg'
    );
  }
  if (options?.projectAutofillFinalize === true) {
    formData.append('projectAutofillFinalize', 'true');
  }
  return formData;
}

function getChatbotDashboardBaseUrl(): string {
  const env = environment as {
    env?: string;
    localApi?: { dashboard?: string };
    api: { dashboard: string };
  };
  const localBaseURL = env.localApi?.dashboard;
  const primaryBaseURL = env.api.dashboard;
  const shouldTryLocal = env.env === 'local' && Boolean(localBaseURL);
  const base = (shouldTryLocal ? localBaseURL : primaryBaseURL) || primaryBaseURL;
  return String(base).replace(/\/$/, '');
}

function getChatbotMessageStreamUrl(): string {
  return `${getChatbotDashboardBaseUrl()}/chatbot/message/stream`;
}

function getChatbotMessageAsyncUrl(): string {
  return `${getChatbotDashboardBaseUrl()}/chatbot/message/async`;
}

async function throwFromStreamHttpError(
  response: Response,
  query: string,
  historyLength: number
): Promise<never> {
  const responseStatus = response.status;
  const text = await response.text();
  let responseData: Record<string, unknown> | null = null;
  if (text) {
    try {
      responseData = JSON.parse(text) as Record<string, unknown>;
    } catch {
      responseData = { message: text };
    }
  }
  const responseMessage =
    (typeof responseData?.message === 'string' && responseData.message) ||
    (typeof responseData?.error === 'string' && responseData.error) ||
    '';

  if (responseStatus === 413 && responseData?.code === 'FILE_TOO_LARGE') {
    const maxBytes =
      typeof responseData.maxBytes === 'number' && Number.isFinite(responseData.maxBytes)
        ? responseData.maxBytes
        : undefined;
    const serverMsg = typeof responseData.message === 'string' ? responseData.message : '';
    const userId413 = getUserId();
    fireAnalytics('agent_message_error', {
      edify_user_id: userId413,
      session_id: getSessionId(),
      device_category: getDeviceCategory(),
      category: 'agent',
      action: 'message_error',
      query_length: query.length,
      history_length: historyLength,
      error_type: 'file_too_large'
    });
    const tooLargeErr = new Error('FILE_TOO_LARGE') as LLMServiceError;
    tooLargeErr.code = 'FILE_TOO_LARGE';
    tooLargeErr.status = 413;
    tooLargeErr.maxBytes = maxBytes;
    tooLargeErr.userMessage = fileTooLargeUserMessage(maxBytes, serverMsg);
    throw tooLargeErr;
  }

  const isDraftNotFound =
    responseStatus === 404 &&
    typeof responseMessage === 'string' &&
    responseMessage.toLowerCase().includes('draft no encontrado');

  const userId = getUserId();
  fireAnalytics('agent_message_error', {
    edify_user_id: userId,
    session_id: getSessionId(),
    device_category: getDeviceCategory(),
    category: 'agent',
    action: 'message_error',
    query_length: query.length,
    history_length: historyLength,
    error_type: isDraftNotFound
      ? 'draft_not_found'
      : 'unknown_error'
  });

  const serviceError = new Error(
    isDraftNotFound ? 'DRAFT_NOT_FOUND' : 'Error conectando con el servicio de análisis'
  ) as LLMServiceError;
  if (isDraftNotFound) {
    serviceError.code = 'DRAFT_NOT_FOUND';
    serviceError.status = 404;
  }
  throw serviceError;
}

function processNdjsonLine(
  trimmed: string,
  onPipelineEvent: (event: ProjectAutofillStreamPipelineEvent) => void,
  touchStreamActivity: () => void,
  query: string,
  historyLength: number
): LLMAnalysisResult | null {
  let obj: unknown;
  try {
    obj = JSON.parse(trimmed);
  } catch {
    return null;
  }
  if (!obj || typeof obj !== 'object' || !('type' in obj)) {
    return null;
  }
  const ev = obj as NdjsonChatbotEvent;
  if (ev.type === 'progress' && typeof ev.phase === 'string') {
    touchStreamActivity();
    onPipelineEvent({
      kind: 'progress',
      phase: ev.phase,
      draftId: typeof ev.draftId === 'string' ? ev.draftId : undefined
    });
    return null;
  }
  if (ev.type === 'heartbeat') {
    touchStreamActivity();
    const phase = typeof ev.phase === 'string' && ev.phase.length > 0 ? ev.phase : undefined;
    onPipelineEvent({ kind: 'heartbeat', phase });
    return null;
  }
  if (ev.type === 'complete' && ev.data && typeof ev.data === 'object') {
    touchStreamActivity();
    return ev.data as LLMAnalysisResult;
  }
  if (ev.type === 'error') {
    touchStreamActivity();
    const msg = typeof ev.message === 'string' ? ev.message : 'Algo salió mal'
    const err = new Error(msg) as LLMServiceError;
    err.code = typeof ev.code === 'string' ? ev.code : 'CHATBOT_STREAM_ERROR';
    const userId = getUserId();
    fireAnalytics('agent_message_error', {
      edify_user_id: userId,
      session_id: getSessionId(),
      device_category: getDeviceCategory(),
      category: 'agent',
      action: 'message_error',
      query_length: query.length,
      history_length: historyLength,
      error_type: err.code
    });
    throw err;
  }
  return null;
}

export async function analyzeWithLLMProjectAutofillStream(
  query: string,
  history: ConversationMessage[] | undefined,
  options: AnalyzeWithLLMOptions | undefined,
  onPipelineEvent: (event: ProjectAutofillStreamPipelineEvent) => void
): Promise<LLMAnalysisResult> {
  const payloadHistory = history || [];
  const formData = buildChatbotMessageFormData(query, payloadHistory, options);
  const authToken = localStorage.getItem('access-Token-Edify');
  const streamUrl = getChatbotMessageStreamUrl();
  const controller = new AbortController();
  const requestStartedAt = Date.now();
  const absoluteDeadline = requestStartedAt + PROJECT_AUTOFILL_STREAM_MAX_DURATION_MS;
  let inactivityTimerId: number | null = null;
  let streamAbortReason: 'inactivity' | 'absolute' | null = null;

  const clearInactivityTimer = () => {
    if (inactivityTimerId != null) {
      window.clearTimeout(inactivityTimerId);
      inactivityTimerId = null;
    }
  };

  const touchStreamActivity = () => {
    clearInactivityTimer();
    inactivityTimerId = window.setTimeout(() => {
      streamAbortReason = 'inactivity';
      controller.abort();
    }, PROJECT_AUTOFILL_STREAM_INACTIVITY_MS);
  };

  let completeData: LLMAnalysisResult | null = null;
  try {
    const response = await fetch(streamUrl, {
      method: 'POST',
      headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      body: formData,
      signal: controller.signal
    });

    if (response.status === 404 || response.status === 501) {
      return analyzeWithLLM(query, history, options);
    }

    if (!response.ok) {
      await throwFromStreamHttpError(response, query, payloadHistory.length);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Respuesta en formato inesperado');
    }

    touchStreamActivity();

    const decoder = new TextDecoder();
    let carry = '';

    while (true) {
      if (Date.now() >= absoluteDeadline) {
        streamAbortReason = 'absolute';
        controller.abort();
      }
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      carry += decoder.decode(value, { stream: true });
      const parts = carry.split('\n');
      carry = parts.pop() ?? '';
      for (const line of parts) {
        const trimmed = line.trim();
        if (!trimmed) {
          continue;
        }
        const result = processNdjsonLine(
          trimmed,
          onPipelineEvent,
          touchStreamActivity,
          query,
          payloadHistory.length
        );
        if (result) {
          completeData = result;
        }
      }
    }

    const tail = carry.trim();
    if (tail) {
      const result = processNdjsonLine(
        tail,
        onPipelineEvent,
        touchStreamActivity,
        query,
        payloadHistory.length
      );
      if (result) {
        completeData = result;
      }
    }

    if (!completeData) {
      throw new Error('Respuesta en formato inesperado');
    }

    const userId = getUserId();
    fireAnalytics('agent_message_sent', {
      edify_user_id: userId,
      session_id: getSessionId(),
      device_category: getDeviceCategory(),
      category: 'agent',
      action: 'message_sent',
      query_length: query.length,
      action_id: completeData.actionId || null,
      confidence: completeData.confidence || null,
      guide_mode: completeData.guideMode || false,
      has_data: !!completeData.data,
      needs_data: completeData.needsData || false,
      history_length: payloadHistory.length
    });

    return completeData;
  } catch (error) {
    const err = error as Error & { name?: string };
    if (err?.name === 'AbortError') {
      const userId = getUserId();
      fireAnalytics('agent_message_error', {
        edify_user_id: userId,
        session_id: getSessionId(),
        device_category: getDeviceCategory(),
        category: 'agent',
        action: 'message_error',
        query_length: query.length,
        history_length: payloadHistory.length,
        error_type: 'stream_timeout'
      });
      const timeoutErr = new Error('STREAM_TIMEOUT') as LLMServiceError;
      timeoutErr.code = 'STREAM_TIMEOUT';
      timeoutErr.userMessage =
        streamAbortReason === 'inactivity'
          ? 'El proceso dejó de enviar actualizaciones por un tiempo prolongado. Intentá de nuevo.'
          : 'El análisis del documento tardó demasiado. Intentá de nuevo en unos minutos.';
      throw timeoutErr;
    }
    throw error;
  } finally {
    clearInactivityTimer();
  }
}

export type ProjectAutofillChatAsyncAcceptedPayload = {
  conversationId: string;
  draftId: string;
  phase: string;
  detailPhase?: string;
  documentationPipelinePath?: string;
};

export type AnalyzeWithLLMProjectAutofillAsyncExtra = {
  onAsyncAccepted?: (payload: ProjectAutofillChatAsyncAcceptedPayload) => void | Promise<void>
  multiDocumentation?: boolean
}

function parseChatbotMessageAsyncAccepted(raw: unknown): ProjectAutofillChatAsyncAcceptedPayload | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const root = raw as Record<string, unknown>;
  const data =
    root.data && typeof root.data === 'object' ? (root.data as Record<string, unknown>) : root;
  const conversationId = data.conversationId != null ? String(data.conversationId).trim() : '';
  const draftId = data.draftId != null ? String(data.draftId).trim() : '';
  const phase = typeof data.phase === 'string' && data.phase.length > 0 ? data.phase : 'processing';
  const detailPhaseRaw = data.detailPhase;
  const detailPhase =
    typeof detailPhaseRaw === 'string' && detailPhaseRaw.trim().length > 0
      ? detailPhaseRaw.trim()
      : undefined;
  const documentationPipelinePath =
    typeof data.documentationPipelinePath === 'string' ? data.documentationPipelinePath : undefined;
  if (!/^\d+$/.test(conversationId) || !draftId) {
    return null;
  }
  return { conversationId, draftId, phase, detailPhase, documentationPipelinePath };
}

export async function analyzeWithLLMProjectAutofillAsync(
  query: string,
  history: ConversationMessage[] | undefined,
  options: AnalyzeWithLLMOptions | undefined,
  onPipelineEvent: (event: ProjectAutofillStreamPipelineEvent) => void,
  extra?: AnalyzeWithLLMProjectAutofillAsyncExtra
): Promise<LLMAnalysisResult> {
  const payloadHistory = history || [];
  const formData = buildChatbotMessageFormData(query, payloadHistory, options);
  const authToken = localStorage.getItem('access-Token-Edify');
  const asyncUrl = getChatbotMessageAsyncUrl();

  const response = await fetch(asyncUrl, {
    method: 'POST',
    headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
    body: formData
  });

  if (response.status === 404 || response.status === 501) {
    if (extra?.multiDocumentation) {
      const err = new Error('DOCUMENTATION_ASYNC_UNAVAILABLE') as LLMServiceError
      err.code = 'DOCUMENTATION_ASYNC_UNAVAILABLE'
      err.userMessage =
        'No se pudo iniciar el análisis de varios archivos. Probá de nuevo en unos minutos.'
      throw err
    }
    return analyzeWithLLMProjectAutofillStream(query, history, options, onPipelineEvent);
  }

  if (response.status !== 202) {
    await throwFromStreamHttpError(response, query, payloadHistory.length);
  }

  let json: unknown;
  try {
    json = await response.json();
  } catch {
    throw new Error('Respuesta en formato inesperado');
  }

  const accepted = parseChatbotMessageAsyncAccepted(json);
  if (!accepted) {
    throw new Error('Respuesta en formato inesperado');
  }

  if (extra?.onAsyncAccepted) {
    await extra.onAsyncAccepted(accepted);
  }

  onPipelineEvent({
    kind: 'progress',
    phase: documentationPipelinePhaseForLoader({
      phase: accepted.phase,
      detailPhase: accepted.detailPhase
    }),
    draftId: accepted.draftId
  });

  await waitForProjectAutofillDocumentationPipeline(
    accepted.draftId,
    ({ loaderPhase, status }) => {
    onPipelineEvent({
      kind: 'progress',
      phase: loaderPhase,
      draftId: accepted.draftId,
      primaryFileOriginalName: status.primaryFileOriginalName ?? null,
      primaryFileSizeBytes: status.primaryFileSizeBytes ?? null
    });
  }
  );

  const finalizeCollected = {
    ...(options?.collectedData && typeof options.collectedData === 'object' ? options.collectedData : {}),
    draftId: accepted.draftId
  };

  const finalizeOptions: AnalyzeWithLLMOptions = {
    ...options,
    conversationId: accepted.conversationId,
    draftId: accepted.draftId,
    collectedData: finalizeCollected,
    image: undefined,
    documentationFiles: undefined,
    projectAutofillFinalize: true
  };

  return analyzeWithLLM(query, history, finalizeOptions);
}

export function fileTooLargeUserMessage(maxBytes?: number, serverMessage?: string): string {
  const trimmed = typeof serverMessage === 'string' ? serverMessage.trim() : '';
  if (trimmed) return trimmed;
  if (typeof maxBytes === 'number' && maxBytes > 0) {
    const mb = maxBytes / (1024 * 1024);
    const label = mb >= 10 ? String(Math.round(mb)) : String(Math.round(mb * 10) / 10);
    return `El archivo supera el límite permitido (${label} MB como máximo). Elegí un archivo más chico.`;
  }
  return 'El archivo es demasiado grande. Elegí uno más chico e intentá de nuevo.';
}

const getAuthHeaders = () => {
  const authToken = localStorage.getItem("access-Token-Edify");
  return { headers: { Authorization: `Bearer ${authToken}` } };
};

const getUserId = (): string => {
  try {
    const state = JSON.parse(localStorage.getItem('persist:general') || '{}');
    const generalState = JSON.parse(state.general || '{}');
    return generalState.userData?.id || '';
  } catch (e) {
    return '';
  }
};

export const analyzeWithLLM = async (
  query: string, 
  history?: ConversationMessage[],
  options?: AnalyzeWithLLMOptions
): Promise<LLMAnalysisResult> => {
  try {
    const payloadHistory = history || [];
    const formData = buildChatbotMessageFormData(query, payloadHistory, options);

    const response = await axios.post<{
      message: string; 
      data: LLMAnalysisResult;
    }>(
      '/chatbot/message',
      formData,
      getAuthHeaders()
    );
    
    const responseData = response.data.data;
    
    if (!responseData) {
      throw new Error('Respuesta en formato inesperado');
    }
    
    const userId = getUserId();
    fireAnalytics("agent_message_sent", {
      edify_user_id: userId,
      session_id: getSessionId(),
      device_category: getDeviceCategory(),
      category: "agent",
      action: "message_sent",
      query_length: query.length,
      action_id: responseData.actionId || null,
      confidence: responseData.confidence || null,
      guide_mode: responseData.guideMode || false,
      has_data: !!responseData.data,
      needs_data: responseData.needsData || false,
      history_length: payloadHistory.length
    });
    
    return responseData;
  } catch (error) {
    const rawError = error as any;
    const responseStatus = rawError?.response?.status;
    const responseData = rawError?.response?.data;
    const responseMessage =
      responseData?.message ||
      rawError?.response?.data?.error ||
      rawError?.response?.data?.data?.message ||
      '';

    if (responseStatus === 413 && responseData?.code === 'FILE_TOO_LARGE') {
      const maxBytes =
        typeof responseData.maxBytes === 'number' && Number.isFinite(responseData.maxBytes)
          ? responseData.maxBytes
          : undefined;
      const serverMsg = typeof responseData.message === 'string' ? responseData.message : '';
      const userId413 = getUserId();
      fireAnalytics("agent_message_error", {
        edify_user_id: userId413,
        session_id: getSessionId(),
        device_category: getDeviceCategory(),
        category: "agent",
        action: "message_error",
        query_length: query.length,
        history_length: history?.length || 0,
        error_type: "file_too_large"
      });
      const tooLargeErr = new Error('FILE_TOO_LARGE') as LLMServiceError;
      tooLargeErr.code = 'FILE_TOO_LARGE';
      tooLargeErr.status = 413;
      tooLargeErr.maxBytes = maxBytes;
      tooLargeErr.userMessage = fileTooLargeUserMessage(maxBytes, serverMsg);
      throw tooLargeErr;
    }

    const isDraftNotFound =
      responseStatus === 404 &&
      typeof responseMessage === 'string' &&
      responseMessage.toLowerCase().includes('draft no encontrado');

    const userId = getUserId();
    fireAnalytics("agent_message_error", {
      edify_user_id: userId,
      session_id: getSessionId(),
      device_category: getDeviceCategory(),
      category: "agent",
      action: "message_error",
      query_length: query.length,
      history_length: history?.length || 0,
      error_type: isDraftNotFound
        ? "draft_not_found"
        : error instanceof Error
          ? error.message
          : "unknown_error"
    });

    const serviceError = new Error(
      isDraftNotFound ? 'DRAFT_NOT_FOUND' : 'Error conectando con el servicio de análisis'
    ) as LLMServiceError;
    if (isDraftNotFound) {
      serviceError.code = 'DRAFT_NOT_FOUND';
      serviceError.status = 404;
    }
    throw serviceError;
  }
};

export type InterpretSearchParams = {
  skillIds?: number[];
  jurisdictionIds?: number[];
  rol?: string;
  order?: string;
  page?: number;
  take?: number;
  search?: string;
  allCountry?: boolean;
  type?: "PERSON" | "COMPANY";
  identity_validated?: boolean;
  featured?: boolean;
};

export type InterpretSearchLabel = { id: number; name: string };

export type InterpretSearchResponse = {
  matchType: 'exact' | 'similar';
  params: InterpretSearchParams;
  originalQuery: string;
  searchLabel?: string;
  skillLabels?: InterpretSearchLabel[];
  jurisdictionLabels?: InterpretSearchLabel[];
};

export const interpretSearch = async (query: string): Promise<InterpretSearchResponse> => {
  const authToken = localStorage.getItem('access-Token-Edify');
  const headers = { headers: { Authorization: `Bearer ${authToken}` } };

  const response = await axios.post<{
    message: string;
    data: InterpretSearchResponse;
  }>('/chatbot/interpret-search', { query: query.trim() }, headers);

  const data = response.data?.data;
  if (!data?.params) {
    throw new Error('Respuesta en formato inesperado');
  }
  return data;
};

